import React, { useEffect, useMemo, useState } from 'react';
import { Percent, Save, AlertTriangle } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { suggestedRateFor } from '../../utils/commissionRate';

/**
 * Commission rates, per bank.
 *
 * Administrators only. An agent must never be able to set the rate their own
 * commission is worked out from, so this screen is not rendered for anyone
 * else and the rate fields do not appear on any agent-facing form.
 *
 * The rate a collection actually uses is resolved in three layers:
 * the account's own rate, then this bank rate, then the default row below.
 */
const CommissionRatesSettings: React.FC = () => {
  const {
    banks,
    updateBank,
    currentUser,
    commissionSettings,
    updateCommissionRate,
    updateSarfaesiRate,
  } = useSRMS();

  const isAdmin = currentUser.role === 'admin';

  // Local edits, so a half-typed number never reaches the saved rate.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [defaultDraft, setDefaultDraft] = useState<string>(
    String(commissionSettings.defaultRate ?? 10)
  );
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [sarfaesiDraft, setSarfaesiDraft] = useState<string>(
    String(commissionSettings.sarfaesiRate ?? '')
  );

  // The saved rates arrive from the database after this screen has already
  // rendered, so the boxes have to pick them up -- otherwise a rate that IS
  // set shows as blank and looks unsaved.
  useEffect(() => {
    if (commissionSettings.sarfaesiRate) {
      setSarfaesiDraft(String(commissionSettings.sarfaesiRate));
    }
  }, [commissionSettings.sarfaesiRate]);

  useEffect(() => {
    if (commissionSettings.defaultRate) {
      setDefaultDraft(String(commissionSettings.defaultRate));
    }
  }, [commissionSettings.defaultRate]);

  const rows = useMemo(
    () =>
      (banks || [])
        .filter((b) => b.active !== false)
        .map((b) => ({
          bank: b,
          current: b.defaultCommissionRate,
          suggested: suggestedRateFor(b),
        })),
    [banks]
  );

  if (!isAdmin) return null;

  const flash = (msg: string) => {
    setSavedNote(msg);
    window.setTimeout(() => setSavedNote(null), 4000);
  };

  const saveBank = (bankId: string, bankName: string) => {
    const raw = draft[bankId];
    const value = Number(raw);
    if (!raw || !isFinite(value) || value <= 0 || value > 100) {
      flash('Enter a rate between 0 and 100.');
      return;
    }
    updateBank(bankId, { defaultCommissionRate: value });
    setDraft((prev) => {
      const next = { ...prev };
      delete next[bankId];
      return next;
    });
    flash(`${bankName} commission rate saved as ${value}%.`);
  };

  const saveDefault = () => {
    const value = Number(defaultDraft);
    if (!isFinite(value) || value <= 0 || value > 100) {
      flash('Enter a default rate between 0 and 100.');
      return;
    }
    updateCommissionRate(value);
    flash(`Default commission rate saved as ${value}%.`);
  };

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-amber-600" />
          <div>
            <span className="font-bold text-slate-900 block">Commission Rates</span>
            <span className="text-slate-500 text-[11px]">
              Administrators only. A rate set on an individual account
              overrides its bank; a bank without a rate uses the default.
            </span>
          </div>
        </div>
      </div>

      {savedNote && (
        <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-900 font-semibold">
          {savedNote}
        </div>
      )}

      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200 uppercase tracking-wider">
            <tr>
              <th className="p-3">Bank</th>
              <th className="p-3">Current rate</th>
              <th className="p-3">Set rate (%)</th>
              <th className="p-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-700">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-6 text-center text-slate-400">
                  No banks have been added yet.
                </td>
              </tr>
            ) : (
              rows.map(({ bank, current, suggested }) => {
                const value =
                  draft[bank.id] !== undefined
                    ? draft[bank.id]
                    : suggested !== undefined
                    ? String(suggested)
                    : '';
                const unset = current === undefined || current === null;
                return (
                  <tr key={bank.id} className="hover:bg-slate-50 transition">
                    <td className="p-3 font-bold text-slate-900">
                      {bank.name}
                      {bank.code && (
                        <span className="ml-1.5 font-mono text-[10px] text-slate-400">
                          {bank.code}
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-mono font-bold">
                      {unset ? (
                        <span className="text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Using default ({commissionSettings.defaultRate ?? 10}%)
                        </span>
                      ) : (
                        <span className="text-emerald-700">{current}%</span>
                      )}
                    </td>
                    <td className="p-3">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={value}
                        onChange={(e) =>
                          setDraft((prev) => ({ ...prev, [bank.id]: e.target.value }))
                        }
                        className="w-24 bg-white border border-slate-200 rounded-lg px-2 py-1 font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => saveBank(bank.id, bank.name)}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1"
                      >
                        <Save className="w-3 h-3" />
                        Save
                      </button>
                    </td>
                  </tr>
                );
              })
            )}

            {/* SARFAESI. Agents mark which accounts are SARFAESI on their
                commission bill; only an administrator sets what that work is
                worth, so classifying an account can never raise a rate. */}
            <tr className="bg-indigo-50/60">
              <td className="p-3 font-bold text-slate-900">
                SARFAESI
                <span className="block text-[10px] font-medium text-slate-500">
                  Used for accounts marked SARFAESI, whichever bank
                </span>
              </td>
              <td className="p-3 font-mono font-bold">
                {commissionSettings.sarfaesiRate ? (
                  <span className="text-indigo-700">
                    {commissionSettings.sarfaesiRate}%
                  </span>
                ) : (
                  <span className="text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Not set
                  </span>
                )}
              </td>
              <td className="p-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={sarfaesiDraft}
                  onChange={(e) => setSarfaesiDraft(e.target.value)}
                  className="w-24 bg-white border border-slate-200 rounded-lg px-2 py-1 font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </td>
              <td className="p-3 text-right">
                <button
                  type="button"
                  onClick={() => {
                    const res = updateSarfaesiRate(Number(sarfaesiDraft));
                    flash(res.message);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1"
                >
                  <Save className="w-3 h-3" />
                  Save
                </button>
              </td>
            </tr>

            {/* The default row: what a bank with no rate of its own uses. */}
            <tr className="bg-slate-50/60">
              <td className="p-3 font-bold text-slate-900">
                Default
                <span className="block text-[10px] font-medium text-slate-500">
                  Used when a bank has no rate set
                </span>
              </td>
              <td className="p-3 font-mono font-bold text-slate-700">
                {commissionSettings.defaultRate ?? 10}%
              </td>
              <td className="p-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={defaultDraft}
                  onChange={(e) => setDefaultDraft(e.target.value)}
                  className="w-24 bg-white border border-slate-200 rounded-lg px-2 py-1 font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </td>
              <td className="p-3 text-right">
                <button
                  type="button"
                  onClick={saveDefault}
                  className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1"
                >
                  <Save className="w-3 h-3" />
                  Save
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CommissionRatesSettings;
